import { Children, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, SelectHTMLAttributes } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'multiple' | 'size'> & { 'aria-label': string };
type Props = SelectProps & { onSearch?: (query: string) => void; onLoadMore?: () => void; loading?: boolean; loadError?: string };
type Option = { value: string; label: string; searchText: string; disabled: boolean };
const textOf = (node: ReactNode): string => Children.toArray(node).map((child) => isValidElement<{ children?: ReactNode }>(child) ? textOf(child.props.children) : String(child)).join('');

/** A shared single-select. The native control retains form values and validation;
 * the popover stays in the top layer, including inside native modal dialogs. */
export function Select({ children, className = '', onSearch, onLoadMore, loading, loadError, ...props }: Props) {
  const id = useId();
  const nativeRef = useRef<HTMLSelectElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState('');
  const typeahead = useRef({ text: '', at: 0 });
  const options = Children.toArray(children).filter(isValidElement).map((child) => {
    const option = child.props as { value?: string | number; children?: ReactNode; disabled?: boolean; 'data-search'?: string };
    const label = textOf(option.children);
    return { value: String(option.value ?? label), label, searchText: `${label} ${option['data-search'] ?? ''}`, disabled: Boolean(option.disabled) };
  });
  const selected = options.find((option) => option.value === String(props.value ?? '')) ?? options[0];
  const searchable = Boolean(onSearch) || options.length > 7;
  const filtered = options.filter((option) => option.searchText.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  const enabled = filtered.filter((option) => !option.disabled);
  const activeOption = enabled.find((option) => option.value === active);
  const optionId = (value: string) => `${id}-option-${options.findIndex((option) => option.value === value)}`;

  const close = (restoreFocus = false) => {
    popupRef.current?.hidePopover();
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };
  const show = () => {
    if (props.disabled) return;
    setQuery(''); setActive(selected?.value ?? ''); setOpen(true);
    onSearch?.('');
  };
  const choose = (option: Option) => {
    if (option.disabled) return;
    close(true);
    const native = nativeRef.current;
    if (native && option.value !== String(props.value)) {
      native.value = option.value;
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.nativeEvent.isComposing) return;
    if (event.target instanceof HTMLButtonElement && event.target !== triggerRef.current) return;
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(true); return; }
    if (event.key === 'Tab') { if (!onLoadMore && !loadError) close(); return; }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (!open) { show(); return; }
      const current = enabled.findIndex((option) => option.value === active);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
      if (enabled[next]) setActive(enabled[next].value);
      return;
    }
    if (event.key === 'Enter' || (event.key === ' ' && event.target === triggerRef.current)) {
      event.preventDefault();
      if (!open) show();
      else if (activeOption) choose(activeOption);
      return;
    }
    if (event.target === triggerRef.current && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const previous = Date.now() - typeahead.current.at < 600 ? typeahead.current.text : '';
      const text = `${previous}${event.key}`.toLocaleLowerCase();
      typeahead.current = { text, at: Date.now() };
      if (!open) show();
      const match = options.find((option) => !option.disabled && option.label.toLocaleLowerCase().startsWith(text));
      if (match) setActive(match.value);
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    const popup = popupRef.current!;
    const position = () => {
      const bounds = triggerRef.current!.getBoundingClientRect();
      const below = innerHeight - bounds.bottom - 10;
      const above = bounds.top - 10;
      const upward = below < 240 && above > below;
      const width = Math.min(Math.max(bounds.width, 220), innerWidth - 16);
      Object.assign(popup.style, {
        width: `${width}px`, left: `${Math.max(8, Math.min(bounds.left, innerWidth - width - 8))}px`,
        top: upward ? 'auto' : `${bounds.bottom + 4}px`, bottom: upward ? `${innerHeight - bounds.top + 4}px` : 'auto',
        maxHeight: `${Math.max(80, Math.min(340, upward ? above : below))}px`,
      });
    };
    position(); popup.showPopover();
    if (searchable) searchRef.current?.focus();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => { window.removeEventListener('resize', position); window.removeEventListener('scroll', position, true); };
  }, [open, searchable]);
  useEffect(() => {
    if (open && activeOption) document.getElementById(optionId(activeOption.value))?.scrollIntoView({ block: 'nearest' });
  }, [open, active, query]);
  useEffect(() => { if (props.disabled) close(); }, [props.disabled]);

  return <span className={`select-control ${className}`}>
    <button ref={triggerRef} id={props.id} type="button" className="select-trigger" role="combobox" disabled={props.disabled}
      aria-label={props['aria-label']} aria-describedby={props['aria-describedby']} aria-required={props.required} aria-invalid={props['aria-invalid']}
      aria-expanded={open} aria-controls={`${id}-list`} aria-haspopup="listbox" aria-activedescendant={open && activeOption ? optionId(activeOption.value) : undefined}
      onClick={() => open ? close() : show()} onKeyDown={onKeyDown} title={selected?.label}>
      <span>{selected?.label ?? '请选择'}</span><ChevronDown size={14} aria-hidden="true" />
    </button>
    <select {...props} ref={nativeRef} className="select-native" tabIndex={-1} aria-hidden="true" id={undefined} onInvalid={(event) => { event.preventDefault(); triggerRef.current?.focus(); props.onInvalid?.(event); }}>{children}</select>
    <div ref={popupRef} popover="auto" className="select-popup" onKeyDown={onKeyDown} onToggle={(event) => { if (event.newState === 'closed') setOpen(false); }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget) && event.relatedTarget !== triggerRef.current) close();
    }}>
      {searchable && <div className="select-search"><Search size={14} aria-hidden="true" /><input ref={searchRef} role="combobox" aria-label={`搜索${props['aria-label']}`} aria-expanded={open} aria-controls={`${id}-list`} aria-autocomplete="list" aria-activedescendant={activeOption ? optionId(activeOption.value) : undefined} value={query} placeholder="搜索选项…" onChange={(event) => {
        setQuery(event.target.value);
        onSearch?.(event.target.value);
        setActive(options.find((option) => !option.disabled && option.searchText.toLocaleLowerCase().includes(event.target.value.toLocaleLowerCase()))?.value ?? '');
      }} /></div>}
      <div id={`${id}-list`} role="listbox" aria-label={props['aria-label']} className="select-options">
        {filtered.map((option) => <div id={optionId(option.value)} key={option.value} role="option" aria-selected={selected?.value === option.value} aria-disabled={option.disabled || undefined} className={`select-option ${active === option.value ? 'highlighted' : ''}`} onPointerMove={() => { if (!option.disabled) setActive(option.value); }} onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.preventDefault(); choose(option); }}><span>{option.label}</span>{selected?.value === option.value && <Check size={14} aria-hidden="true" />}</div>)}
        {!filtered.length && !loading && !loadError && <div className="select-no-results" role="status">没有匹配的选项</div>}
      </div>
      {loading && <div className="select-no-results" role="status">正在加载…</div>}
      {loadError && <div className="select-no-results" role="alert">{loadError}<button type="button" className="text-button" onClick={() => { searchRef.current?.focus(); onSearch?.(query); }}>重试</button></div>}
      {onLoadMore && <button type="button" className="text-button select-load-more" disabled={loading} onClick={() => { searchRef.current?.focus(); onLoadMore?.(); }}>加载更多节点</button>}
    </div>
  </span>;
}
